/**
 * The portal's route table — driver, operator and dispatcher logins alike.
 *
 * A separate surface from the dashboard: its own token, its own guard
 * (PortalRoute), its own chrome. It must never render the admin
 * Header/Footer or hit the admin session endpoint, so Routes.jsx renders
 * this table for every /portal path before the dashboard shell exists.
 *
 * Kept in its own module (rather than inline in Routes.jsx) so the portal's
 * routing can be tested as it really is without loading every admin page.
 *
 *   /portal/e/:token        enrolment (public — the one-time link): drivers
 *                           and operators, exactly as before
 *   /portal/login           Pickup Hungary office staff, signing in with
 *                           their DASHBOARD account — the dispatch board's
 *                           only door now that no dispatcher login is minted
 *   /portal                 each login's home: operators → /portal/operator,
 *                           dispatchers → /portal/dispatcher, drivers → their day
 *   /portal/trip/:id        a driver's trip (driver logins only)
 *   /portal/operator        Operator Admin: Assignments
 *   /portal/operator/trip/:id   one trip's details and receipts
 *   /portal/operator/fleet      Drivers & vehicles
 *   /portal/operator/payments   Payments / statement
 *   /portal/dispatcher          Dispatch board (PH office staff, read-only)
 *   /portal/dispatcher/trip/:id one trip's details, read-only
 */
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PORTAL_USER_KINDS } from './config/config';
import ScrollMemory from './Components/ScrollMemory';
import PortalRoute from './Pages/Portal/PortalRoute';
import PortalEnrolment from './Pages/Portal/Enrolment';
import PortalStaffLogin from './Pages/Portal/StaffLogin';
import PortalDay from './Pages/Portal';
import PortalTripDetail from './Pages/Portal/TripDetail';
import PortalLanding from './Pages/Portal/Operator/PortalLanding';
import OperatorLayout from './Pages/Portal/Operator/OperatorLayout';
import OperatorAssignments from './Pages/Portal/Operator/Assignments';
import OperatorPayments from './Pages/Portal/Operator/Payments';
import OperatorFleet from './Pages/Portal/Operator/Fleet';
import OperatorTripDetails from './Pages/Portal/Operator/TripDetails';
import DispatcherLayout from './Pages/Portal/Dispatcher/DispatcherLayout';
import DispatcherBoard from './Pages/Portal/Dispatcher/Board';
import DispatcherTripDetails from './Pages/Portal/Dispatcher/TripDetails';

const PORTAL_PREFIX = '/portal';

// The Operator Admin: desktop width, English tab title.
const OPERATOR_TITLE = 'Pickup Hungary · Operator';

// The office board: desktop width, English tab title.
const DISPATCHER_TITLE = 'Pickup Hungary · Dispatch';

export const isPortalPath = (pathname) => pathname === PORTAL_PREFIX
  || pathname.startsWith(`${PORTAL_PREFIX}/`);

export default function PortalRoutes() {
  return (
    <>
      {/*
        Going back returns to the row that was being read, not to the top.
        The dashboard has had this since Orders; the portal never did,
        because Routes.jsx renders this table BEFORE the shell that mounts
        it — so every Back out of a trip landed a driver at the top of their
        day and an operator at the top of a month of assignments.
      */}
      <ScrollMemory />
      <Routes>
        <Route path="/portal/e/:token" element={<PortalEnrolment />} />
        {/*
          Public, like enrolment: there is no session yet. PortalRoute would
          show the gate instead of the form, which is the screen this one
          exists to escape from.
        */}
        <Route path="/portal/login" element={<PortalStaffLogin />} />
        <Route
          path="/portal"
          element={<PortalRoute><PortalLanding driverHome={<PortalDay />} /></PortalRoute>}
        />
        <Route
          path="/portal/operator"
          element={(
            <PortalRoute requireKind={PORTAL_USER_KINDS.OPERATOR} wide title={OPERATOR_TITLE}>
              <OperatorLayout />
            </PortalRoute>
          )}
        >
          <Route index element={<OperatorAssignments />} />
          <Route path="trip/:bookingId" element={<OperatorTripDetails />} />
          <Route path="payments" element={<OperatorPayments />} />
          <Route path="fleet" element={<OperatorFleet />} />
          <Route path="*" element={<Navigate to="/portal/operator" replace />} />
        </Route>
        <Route
          path="/portal/dispatcher"
          element={(
            <PortalRoute requireKind={PORTAL_USER_KINDS.DISPATCHER} wide title={DISPATCHER_TITLE}>
              <DispatcherLayout />
            </PortalRoute>
          )}
        >
          <Route index element={<DispatcherBoard />} />
          <Route path="trip/:bookingId" element={<DispatcherTripDetails />} />
          <Route path="*" element={<Navigate to="/portal/dispatcher" replace />} />
        </Route>
        {/* A driver's own trip: an operator or dispatcher login that follows a
            driver's link is sent back to its own screens. */}
        <Route
          path="/portal/trip/:bookingId"
          element={(
            <PortalRoute requireKind={PORTAL_USER_KINDS.DRIVER}>
              <PortalTripDetail />
            </PortalRoute>
          )}
        />
        <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
      </Routes>
    </>
  );
}
